import { CircleIcon, TriangleAlertIcon } from "lucide-react";
import { ModulesStatesEnum } from "@/routes/area-aggregate/types/enums/modules-states.enum";
import tw from "twin.macro";
import { moduleColors } from "@/styles/modules-colors";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  ErrorAndWarning,
  SeverityCodeEnum,
} from "@/routes/area-aggregate/types/area-state-aggregate.type";
import { SheetsType } from "./types/sheets.type";
import { SheetsEnum } from "./enums/sheets.enum";

type PushSheet = (view: SheetsType) => void;

const openModuleAccess = (
  pushSheet: PushSheet,
  areaId: string,
  moduleAddress: string
) =>
  pushSheet({
    id: SheetsEnum.MODULE_ACCESS,
    canGoBack: true,
    moduleAddress,
    areaId,
  });

type Props = {
  color: string;
  numberOfModules: number;
  modules: string[];
  state: ModulesStatesEnum;
  pushSheet: PushSheet;
  areaId: string;
};

export const ModulesDetails = ({
  color,
  numberOfModules,
  modules,
  state,
  pushSheet,
  areaId,
}: Props) => {
  const MAX_MODULES_TO_DISPLAY = 5;
  const { t: translator } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const visibleModules = showAll
    ? modules
    : modules.slice(0, MAX_MODULES_TO_DISPLAY);
  const hasMore = modules.length > MAX_MODULES_TO_DISPLAY;

  return (
    <div tw="flex flex-col gap-2">
      <p tw="text-sm font-medium flex items-center gap-2">
        <CircleIcon tw="w-4 h-4" color={color} fill={color} />
        <span>{translator(`modulesStates.${state}`)}</span>
        <span tw="text-sm font-light text-gray-500">({numberOfModules})</span>
      </p>

      <ul tw="flex flex-col gap-1">
        {visibleModules.map((module) => (
          <li
            key={module}
            css={[
              tw`text-sm font-medium rounded-xl`,
              color === moduleColors.greenBase && tw`bg-modules-greenBase`,
              color === moduleColors.blueBase && tw`bg-modules-blueBase`,
              color === moduleColors.amberBase && tw`bg-modules-amberBase`,
              color === moduleColors.greyBase && tw`bg-modules-greyBase`,
              color === moduleColors.redBase && tw`bg-modules-redBase`,
              tw`active:bg-white active:text-gray-800`,
            ]}
          >
            <button
              type="button"
              onClick={() => openModuleAccess(pushSheet, areaId, module)}
              tw="w-full block text-left p-2"
            >
              {module}
            </button>
          </li>
        ))}
      </ul>

      {hasMore && (
        <button
          css={[
            tw`self-start text-sm font-normal p-2 rounded-xl w-full text-start text-gray-700`,
            color === moduleColors.greenBase && tw`bg-modules-greenBase/60`,
            color === moduleColors.blueBase && tw`bg-modules-blueBase/60`,
            color === moduleColors.amberBase && tw`bg-modules-amberBase/60`,
            color === moduleColors.greyBase && tw`bg-modules-greyBase/60`,
            color === moduleColors.redBase && tw`bg-modules-redBase/60`,
          ]}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? translator("global.showLess")
            : translator("global.showMore")}
        </button>
      )}
    </div>
  );
};

const ErrorModuleItem = ({
  moduleAddress,
  severityCode,
  severity,
  label,
  doc,
  pushSheet,
  areaId,
}: {
  moduleAddress: string;
  severityCode: SeverityCodeEnum;
  severity: string;
  label: string;
  doc: string;
  pushSheet: PushSheet;
  areaId: string;
}) => {
  return (
    <div
      tw="flex flex-col"
      css={[
        tw`rounded-xl bg-modules-redBase`,
        severityCode === SeverityCodeEnum.CRITICAL && tw`bg-modules-redBase`,
        severityCode === SeverityCodeEnum.WARNING && tw`bg-orange-400`,
      ]}
    >
      <div tw="flex items-center justify-between p-2">
        <p tw="text-sm font-medium flex items-center gap-2">
          <span>
            <TriangleAlertIcon
              tw="w-4 h-4"
              color={
                severityCode === SeverityCodeEnum.CRITICAL ? "red" : "orange"
              }
              fill="white"
            />
          </span>
          <button
            type="button"
            onClick={() => openModuleAccess(pushSheet, areaId, moduleAddress)}
            tw="w-full block text-left"
          >
            {moduleAddress}
          </button>
        </p>
        <p
          css={[
            tw`text-white px-2 py-1 rounded-2xl text-xs`,
            severityCode === SeverityCodeEnum.CRITICAL && tw`bg-red-500`,
            severityCode === SeverityCodeEnum.WARNING && tw`bg-orange-500`,
          ]}
        >
          {severity}
        </p>
      </div>
      <div tw="flex flex-col gap-1 bg-white shadow-sm border border-gray-200 p-2 rounded-bl-lg rounded-br-lg">
        <p tw="text-base font-medium">
          {severityCode === SeverityCodeEnum.CRITICAL
            ? "Erreur critique"
            : "Alerte"}
        </p>
        <p tw="text-sm font-light text-gray-500">{label}</p>
        <p tw="text-sm font-light text-gray-500">{doc}</p>
      </div>
    </div>
  );
};

type ErrorModuleDetailsProps = {
  errorsAndWarnings: ErrorAndWarning[];
  pushSheet: PushSheet;
  areaId: string;
};

export const ErrorModuleDetails = ({
  errorsAndWarnings,
  pushSheet,
  areaId,
}: ErrorModuleDetailsProps) => {
  const MAX_MODULES_TO_DISPLAY = 5;
  const numberOfModules = errorsAndWarnings.length;

  const { t: translator } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const visibleModules = showAll
    ? errorsAndWarnings
    : errorsAndWarnings.slice(0, MAX_MODULES_TO_DISPLAY);

  const hasMore = errorsAndWarnings.length > MAX_MODULES_TO_DISPLAY;

  return (
    <div tw="flex flex-col gap-2">
      <p tw="text-sm font-medium flex items-center gap-2">
        <CircleIcon
          tw="w-4 h-4"
          color={moduleColors.redBase}
          fill={moduleColors.redBase}
        />
        <span>{translator("modulesStates.faulty")}</span>
        <span tw="text-sm font-light text-gray-500">({numberOfModules})</span>
      </p>
      <ul tw="flex flex-col gap-3">
        {visibleModules.map((errorAndWarning) => (
          <li tw="text-sm font-medium" key={errorAndWarning.moduleAddress}>
            <ErrorModuleItem
              moduleAddress={errorAndWarning.moduleAddress}
              severityCode={errorAndWarning.severityCode}
              severity={errorAndWarning.severity.fr}
              label={errorAndWarning.label.fr}
              doc={errorAndWarning.doc.fr}
              pushSheet={pushSheet}
              areaId={areaId}
            />
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          css={[
            tw`self-start text-sm font-normal p-2 rounded-xl w-full text-start text-gray-700`,
            tw`bg-modules-redBase/60`,
          ]}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? translator("global.showLess")
            : translator("global.showMore")}
        </button>
      )}
    </div>
  );
};
